import { useMemo } from 'react';
import { GrantableGrade, ResolvedScopeRow, ScopeEntry } from '../api';

/**
 * SaaS Phase 4 §7 — the grade/subject scope picker.
 *
 * Shared by the school-admin console (granting from the school's entitlements)
 * and the coordinator console (granting from the coordinator's own scope). The
 * only difference between the two is the `grantable` tree passed in, which is
 * what keeps a coordinator's picker from ever rendering a grade they do not
 * hold.
 *
 * This is a convenience, not a control. The server re-validates every grant
 * against the granter's reach, so a tampered payload gains nothing — which is
 * why this component can stay purely presentational.
 */

/** The picker's working state: which grades/subjects are ticked. */
export interface ScopeSelection {
  /** Grade ids granted whole. */
  wholeGrades: Set<string>;
  /** Individually granted subject ids. */
  subjects: Set<string>;
  /** One switch for the whole person — see the note on the download toggle. */
  canDownload: boolean;
}

export const emptySelection = (): ScopeSelection => ({
  wholeGrades: new Set(),
  subjects: new Set(),
  canDownload: false,
});

/** Stored rows -> picker state. */
export function selectionFromRows(rows: ResolvedScopeRow[]): ScopeSelection {
  const wholeGrades = new Set<string>();
  const subjects = new Set<string>();
  for (const r of rows) {
    if (r.subjectId === null) wholeGrades.add(r.gradeId);
    else subjects.add(r.subjectId);
  }
  return {
    wholeGrades,
    subjects,
    // The UI toggle is per-person while the schema stores per-grant, so this
    // collapses the rows: the switch reads "on" if ANY grant carries download.
    // Rows only ever get the flag as a set from this picker, so in practice
    // they are uniform; `some` rather than `every` keeps a mixed set (written
    // by a future per-grant UI) from silently reading as "off" and then being
    // flattened to off on the next save.
    canDownload: rows.some((r) => r.canDownload),
  };
}

/** Picker state -> the payload the API expects. */
export function selectionToEntries(sel: ScopeSelection, tree: GrantableGrade[]): ScopeEntry[] {
  const out: ScopeEntry[] = [];
  for (const g of tree) {
    if (sel.wholeGrades.has(g.id)) {
      // Whole-grade supersedes individual subjects server-side, so the ticked
      // subjects underneath are simply not sent.
      out.push({ gradeId: g.id, subjectId: null, canDownload: sel.canDownload && g.canDownload });
      continue;
    }
    for (const s of g.subjects) {
      if (!sel.subjects.has(s.id)) continue;
      out.push({
        gradeId: g.id,
        subjectId: s.id,
        // Never ask for download on a grant the granter cannot pass it on for.
        // The server would reject the whole payload; clamping here means one
        // un-downloadable subject does not block the rest of the save.
        canDownload: sel.canDownload && s.canDownload,
      });
    }
  }
  return out;
}

const box = 'h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500';

export function ScopePicker({
  tree,
  value,
  onChange,
  disabled,
}: {
  tree: GrantableGrade[];
  value: ScopeSelection;
  onChange: (next: ScopeSelection) => void;
  disabled?: boolean;
}) {
  const anyDownloadable = useMemo(
    () => tree.some((g) => g.canDownload || g.subjects.some((s) => s.canDownload)),
    [tree],
  );

  const update = (fn: (draft: ScopeSelection) => void) => {
    // Sets are mutable, so clone before handing a new object to React — an
    // in-place edit would not re-render.
    const next: ScopeSelection = {
      wholeGrades: new Set(value.wholeGrades),
      subjects: new Set(value.subjects),
      canDownload: value.canDownload,
    };
    fn(next);
    onChange(next);
  };

  const toggleGrade = (g: GrantableGrade) =>
    update((d) => {
      if (d.wholeGrades.has(g.id)) {
        d.wholeGrades.delete(g.id);
      } else {
        d.wholeGrades.add(g.id);
        // Redundant once the whole grade is granted, and leaving them ticked
        // would make un-ticking the grade silently fall back to them.
        for (const s of g.subjects) d.subjects.delete(s.id);
      }
    });

  const toggleSubject = (id: string) =>
    update((d) => {
      if (d.subjects.has(id)) d.subjects.delete(id);
      else d.subjects.add(id);
    });

  if (!tree.length) {
    return (
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
        There is nothing you can grant yet. Ask your administrator to widen your own access first.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {tree.map((g) => {
          const whole = value.wholeGrades.has(g.id);
          return (
            <div key={g.id} className="p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <input
                  type="checkbox"
                  className={box}
                  checked={whole}
                  disabled={disabled || !g.whole}
                  onChange={() => toggleGrade(g)}
                />
                {g.name}
                <span className="text-xs font-normal text-slate-400">
                  {g.whole ? '(entire grade)' : '(selected subjects only)'}
                </span>
              </label>

              <div className="mt-2 ml-6 flex flex-wrap gap-x-4 gap-y-1">
                {g.subjects.map((s) => (
                  <label
                    key={s.id}
                    className={
                      'flex items-center gap-2 text-sm ' +
                      (whole ? 'text-slate-400' : 'text-slate-700')
                    }
                  >
                    <input
                      type="checkbox"
                      className={box}
                      // A whole-grade tick already covers every subject, so
                      // these render checked-and-locked rather than
                      // unchecked-and-misleading.
                      checked={whole || value.subjects.has(s.id)}
                      disabled={disabled || whole}
                      onChange={() => toggleSubject(s.id)}
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className={box}
          checked={value.canDownload}
          disabled={disabled || !anyDownloadable}
          onChange={() => update((d) => (d.canDownload = !d.canDownload))}
        />
        Allow downloads
        <span className="text-xs text-slate-400">
          {anyDownloadable
            ? 'applies to everything granted above'
            : 'you do not hold download rights to pass on'}
        </span>
      </label>
    </div>
  );
}
